import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, LocationId, OrderType } from '../game/types';
import { AudioManager } from '../audio/AudioManager';
import { CELL_LOCATIONS } from '../utils/Constants';

export class NinePadDial {
  private buttons: HTMLElement[] = [];

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onAction: (action: OrderType | 'deselect' | 'cancel_synthetize' | 'cancel_rest', ...args: unknown[]) => void
  ) {
    this.setupDial();
    this.update();
  }

  private setupDial(): void {
    this.container.className = 'nine-pad-dial';
    this.container.innerHTML = '';
    this.buttons = [];

    for (let i = 0; i < 9; i++) {
      const button = document.createElement('button');
      button.className = 'dial-button';
      button.dataset.index = i.toString();
      button.addEventListener('click', () => this.handleClick(i));
      
      // Create structure for button content and hotkey indicator
      const buttonContent = document.createElement('div');
      buttonContent.className = 'button-content';
      button.appendChild(buttonContent);
      
      const hotkeyIndicator = document.createElement('span');
      hotkeyIndicator.className = 'hotkey-indicator';
      button.appendChild(hotkeyIndicator);
      
      this.container.appendChild(button);
      this.buttons.push(button);
    }
  }

  private getHotkeyLabel(index: number): string {
    const hotkeys = this.gameData.hotkeys;
    const ninePadRows = [
      hotkeys.nine_pad.top_row,
      hotkeys.nine_pad.middle_row,
      hotkeys.nine_pad.bottom_row
    ];

    for (const row of ninePadRows) {
      const key = row.find((k: any) => k.index === index);
      if (key) {
        return key.label;
      }
    }
    return '';
  }

  update(): void {
    const selected = this.gameState.getSelectedEntity();
    const strings = this.gameData.uiStrings;
    const availableActions: Array<{ label: string; action: OrderType | 'deselect' | 'cancel_synthetize' | 'cancel_rest'; args?: unknown[]; disabled?: boolean }> = [];

    if (selected.type === 'researcher') {
      const researcher = this.gameState.getResearcher(selected.id as string);
      if (researcher) {
        const researcherActions = this.getResearcherActions(researcher);
        // Filter out nulls to get only available actions
        for (const action of researcherActions) {
          if (action !== null) {
            availableActions.push(action);
          }
        }
      }
    }

    // Fill actions array: available actions fill positions 0-7 left-to-right, top-to-bottom
    // Deselect always goes in position 8 (bottom-right) if something is selected
    const actions: Array<{ label: string; action: string; args?: any[]; disabled?: boolean } | null> = [];
    
    // Fill positions 0-7 with available actions
    for (let i = 0; i < 8; i++) {
      if (i < availableActions.length) {
        actions.push(availableActions[i]);
      } else {
        actions.push(null);
      }
    }
    
    // Position 8 (bottom-right) is always deselect if something is selected
    if (selected.type !== null) {
      actions.push({ label: strings.buttons?.deselect || 'Deselect', action: 'deselect' });
    } else {
      actions.push(null);
    }

    this.buttons.forEach((button, index) => {
      const action = actions[index];
      const buttonContent = button.querySelector('.button-content') as HTMLElement;
      const hotkeyIndicator = button.querySelector('.hotkey-indicator') as HTMLElement;
      
      if (action) {
        if (buttonContent) {
          buttonContent.textContent = action.label;
        }
        button.classList.remove('empty');
        if (action.disabled) {
          button.classList.add('disabled');
          button.dataset.action = ''; // Clear action so it can't be clicked
        } else {
          button.classList.remove('disabled');
          button.dataset.action = action.action;
        }
        // Add tooltip key - use special key for disabled actions
        if (action.disabled) {
          if (action.action === 'recruit') {
            button.dataset.tooltipKey = 'action.recruit_disabled';
          } else if (action.action === 'synthetize') {
            button.dataset.tooltipKey = 'action.synthetize_disabled';
          } else if (action.action === 'rest') {
            button.dataset.tooltipKey = 'action.rest_disabled';
          } else if (action.action === 'research' || action.action === 'coredump') {
            button.dataset.tooltipKey = 'action.research_busy';
          } else {
            button.dataset.tooltipKey = `action.${action.action}`;
          }
        } else {
          button.dataset.tooltipKey = `action.${action.action}`;
        }
        // Store args as JSON if they exist
        if (action.args && action.args.length > 0) {
          button.dataset.args = JSON.stringify(action.args);
        } else {
          delete button.dataset.args;
        }
        
        // Show hotkey indicator
        const hotkeyLabel = this.getHotkeyLabel(index);
        if (hotkeyIndicator && hotkeyLabel) {
          hotkeyIndicator.textContent = `[${hotkeyLabel}]`;
          hotkeyIndicator.style.display = '';
        }
      } else {
        if (buttonContent) {
          buttonContent.textContent = '';
        }
        button.classList.add('empty');
        button.classList.remove('disabled');
        delete button.dataset.action;
        delete button.dataset.args;
        delete button.dataset.tooltipKey;
        
        // Hide hotkey indicator for empty buttons
        if (hotkeyIndicator) {
          hotkeyIndicator.textContent = '';
          hotkeyIndicator.style.display = 'none';
        }
      }
    });
  }

  private getResearcherActions(researcher: Researcher): Array<{ label: string; action: string; args?: any[] } | null> {
    const strings = this.gameData.uiStrings;
    const state = this.gameState.getState();
    const actions: Array<{ label: string; action: string; args?: any[] } | null> = [];

    // CRITICAL: If researcher is containing, don't allow ANY other actions
    if (researcher.currentAction === 'Contain') {
      // Only show empty slots - no actions available while containing
      for (let i = 0; i < 8; i++) {
        actions.push(null);
      }
      return actions;
    }

    // Move - available unless containing
    actions.push({ label: strings.buttons?.move || 'Move', action: 'move' });

    // Research - if at a cell with anomaly (not breached FROM here)
    const cells = CELL_LOCATIONS;
    const researchCell = cells.find(cell => {
      const anomaly = this.gameState.getAnomalyInCell(cell);
      const breachedFromHere = this.gameState.getBreachedFromCell(cell);
      return anomaly && !breachedFromHere && researcher.location === cell;
    });
    if (researchCell) {
      // Check if another researcher is already researching this cell
      const researchers = this.gameState.getResearchers();
      const otherResearcher = researchers.find(r => 
        r.id !== researcher.id && 
        r.currentAction === 'Research' && 
        r.location === researchCell
      );

      if (otherResearcher) {
        actions.push({ label: strings.buttons?.research || 'Research', action: 'research', disabled: true });
      } else {
        actions.push({ label: strings.buttons?.research || 'Research', action: 'research' });
      }
    } else {
      actions.push(null);
    }

    // Contain - if at a cell with breached anomaly
    const containCell = cells.find(cell => {
      const breached = this.gameState.getBreachedInCell(cell);
      return breached && researcher.location === cell && !breached.isContaining;
    });
    if (containCell) {
      actions.push({ label: strings.buttons?.contain || 'Contain', action: 'contain', args: [containCell] });
    } else {
      actions.push(null);
    }

    // Craft - if in control room
    if (researcher.location === 'control_room' && researcher.currentAction === null) {
      actions.push({ label: strings.buttons?.craft || 'Craft', action: 'craft' });
    } else {
      actions.push(null);
    }

    // Accept - if in empty cell
    const acceptCell = cells.find(cell => {
      const anomaly = this.gameState.getAnomalyInCell(cell);
      return !anomaly && researcher.location === cell;
    });
    if (acceptCell) {
      actions.push({ label: strings.buttons?.accept || 'Accept', action: 'accept', args: [acceptCell] });
    } else {
      actions.push(null);
    }

    // Coredump - if at cell with anomaly with 2+ known properties (not breached FROM here)
    const coredumpCell = cells.find(cell => {
      const anomaly = this.gameState.getAnomalyInCell(cell);
      const breachedFromHere = this.gameState.getBreachedFromCell(cell);
      return anomaly && !breachedFromHere && anomaly.knownProperties.length >= 2 && researcher.location === cell;
    });
    if (coredumpCell) {
      // Check if another researcher is already researching this cell
      const researchers = this.gameState.getResearchers();
      const otherResearcher = researchers.find(r => 
        r.id !== researcher.id && 
        r.currentAction === 'Research' && 
        r.location === coredumpCell
      );

      if (otherResearcher) {
        actions.push({ label: strings.buttons?.coredump || 'Coredump', action: 'coredump', args: [coredumpCell], disabled: true });
      } else {
        actions.push({ label: strings.buttons?.coredump || 'Coredump', action: 'coredump', args: [coredumpCell] });
      }
    } else {
      actions.push(null);
    }

    // Recruit - if in control room and can recruit and pool has available researchers
    if (researcher.location === 'control_room' && researcher.currentAction === null) {
      if (this.gameState.canAddResearcher() && this.gameState.hasAvailableResearchers()) {
        actions.push({ label: strings.buttons?.recruit || 'Recruit', action: 'recruit' });
      } else {
        // Show recruit button as disabled if at max or pool empty
        actions.push({ label: strings.buttons?.recruit || 'Recruit', action: 'recruit', disabled: true });
      }
    } else {
      actions.push(null);
    }

    // Synthetize - if in control room and no active action (or already synthesizing)
    if (researcher.location === 'control_room') {
      if (researcher.currentAction === 'Synthetize') {
        actions.push({ label: strings.buttons?.cancel || 'Cancel', action: 'cancel_synthetize' });
      } else if (researcher.currentAction === null) {
        // Check if chemicals are at maximum capacity
        const state = this.gameState.getState();
        const chemicals = state.chemicals;
        const maxChemicals = state.maxChemicals;
        const isAtMaxCapacity = chemicals >= maxChemicals;
        
        if (isAtMaxCapacity) {
          // Show synthetize button as disabled when at max capacity
          actions.push({ label: strings.buttons?.synthetize || 'Synthetize', action: 'synthetize', disabled: true });
        } else {
          actions.push({ label: strings.buttons?.synthetize || 'Synthetize', action: 'synthetize' });
        }
      } else {
        actions.push(null); // Has other action, can't synth
      }
    } else {
      actions.push(null);
    }

    // Rest - if in control room and no active action (or already resting)
    if (researcher.location === 'control_room') {
      if (researcher.currentAction === 'Rest') {
        actions.push({ label: strings.buttons?.cancel || 'Cancel', action: 'cancel_rest' });
      } else if (researcher.currentAction === null) {
        // Check if sanity is at max and if there are insane researchers
        const maxSanity = researcher.data.sanity;
        const isAtMaxSanity = researcher.currentSanity >= maxSanity;
        const hasInsaneResearchers = this.hasInsaneResearchers(researcher.id);
        
        if (isAtMaxSanity && !hasInsaneResearchers) {
          // Show rest button as disabled when at max sanity and no insane researchers
          actions.push({ label: strings.buttons?.rest || 'Rest', action: 'rest', disabled: true });
        } else {
          actions.push({ label: strings.buttons?.rest || 'Rest', action: 'rest' });
        }
      } else {
        actions.push(null); // Has other action, can't rest
      }
    } else {
      actions.push(null);
    }

    return actions;
  }

  clickButton(index: number): void {
    this.handleClick(index);
  }

  private handleClick(index: number): void {
    AudioManager.getInstance().playClick();
    const button = this.buttons[index];
    const action = button.dataset.action;
    if (!action || button.classList.contains('disabled')) return;

    // Visual feedback
    button.classList.add('clicked');
    setTimeout(() => button.classList.remove('clicked'), 200);

    if (action === 'deselect') {
      this.gameState.setSelectedEntity(null, null);
      // Don't call this.update() here - let UIManager handle the update
      return;
    }

    const selected = this.gameState.getSelectedEntity();
    if (selected.type === 'researcher') {
      const researcher = this.gameState.getResearcher(selected.id as string);
      if (!researcher) return;

      // Get args from stored data or compute from context
      let args: unknown[] = [];
      if (button.dataset.args) {
        try {
          args = JSON.parse(button.dataset.args);
        } catch (e) {
          // Fallback to computing args if parsing fails
          args = [];
        }
      }
      
      // If no stored args, compute them (fallback for actions that need context)
      if (args.length === 0 && (action === 'accept' || action === 'coredump')) {
        const cells = CELL_LOCATIONS;
        const targetCell = cells.find(cell => {
          if (action === 'accept') {
            const anomaly = this.gameState.getAnomalyInCell(cell);
            return !anomaly && researcher.location === cell;
          } else {
            const anomaly = this.gameState.getAnomalyInCell(cell);
            return anomaly && anomaly.knownProperties.length >= 2 && researcher.location === cell;
          }
        });
        if (targetCell) args.push(targetCell);
      }

      this.onAction(action, ...args);
    } else if (selected.type === 'location') {
      // Location selected - only deselect should work
      if (action === 'deselect') {
        this.onAction(action);
      }
    }
  }

  private hasInsaneResearchers(excludeResearcherId?: string): boolean {
    // Check if there are any researchers with sanity below the insane threshold
    const researchers = this.gameState.getResearchers();
    const insaneThreshold = this.gameData.insanityConfig.insane_threshold;
    for (const r of researchers) {
      if (excludeResearcherId && r.id === excludeResearcherId) {
        continue; // Don't check the researcher who is resting
      }
      const maxSanity = r.data.sanity;
      const relativeSanity = r.currentSanity / maxSanity;
      if (relativeSanity < insaneThreshold) {
        return true; // Found an insane researcher
      }
    }
    return false;
  }
}

